import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { UiMoleculeInputComponent } from '../ui-molecule-input/ui-molecule-input.component';
import { PhoneInputComponent } from '../../atoms/ui-atom-phone-input/ui-atom-phone-input.component';
import { UiAtomCountrySelectComponent } from '../../atoms/ui-atom-country-select/ui-atom-country-select.component';
import { UiAtomCheckboxComponent } from '../../atoms/ui-atom-checkbox/ui-atom-checkbox.component';

import { todayISODate } from '@core/utils/validators/past-or-today-date.validator';
import {
  BENEFICIARY_FIELD_LIMITS,
  BENEFICIARY_GENDER_OPTIONS,
  BeneficiaryFormMode,
} from './beneficiary-form.shared';

/**
 * Formulario presentacional del beneficiario: campos, validaciones (vía el
 * FormGroup recibido), mensajes de error i18n y desplegable de género.
 *
 * Fuente única de verdad reutilizada por:
 * - `ui-molecule-dialog-beneficiary-upsert` (alta/edición desde el panel admin).
 * - `public-form-register` (autoregistro en el flujo público).
 *
 * El componente NO renderiza botones ni hace peticiones: el padre aporta su
 * propia carcasa (diálogo / página) y la semántica de envío.
 */
@Component({
  selector: 'ui-molecule-beneficiary-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    UiMoleculeInputComponent,
    PhoneInputComponent,
    UiAtomCountrySelectComponent,
    UiAtomCheckboxComponent,
  ],
  templateUrl: './ui-molecule-beneficiary-form.component.html',
  styleUrl: './ui-molecule-beneficiary-form.component.scss',
})
export class UiMoleculeBeneficiaryFormComponent {
  /** FormGroup creado con `createBeneficiaryForm()` y propiedad del padre. */
  @Input({ required: true }) form!: FormGroup;
  /** Gobierna la visibilidad/obligatoriedad del consentimiento GDPR. */
  @Input() mode: BeneficiaryFormMode = 'create';
  /** El padre lo activa al intentar enviar para forzar la visibilidad de errores. */
  @Input() submitted = false;

  readonly limits = BENEFICIARY_FIELD_LIMITS;
  readonly genderOptions = BENEFICIARY_GENDER_OPTIONS;
  readonly todayISO = todayISODate();

  constructor(private translate: TranslateService) {}

  get firstNameControl() {
    return this.form.get('firstName');
  }
  get lastNameControl() {
    return this.form.get('lastName');
  }
  get emailControl() {
    return this.form.get('email');
  }
  get phoneControl() {
    return this.form.get('phone');
  }
  get birthDateControl() {
    return this.form.get('birthDate');
  }
  get genderControl() {
    return this.form.get('gender');
  }
  get birthCountryControl() {
    return this.form.get('birthCountry');
  }
  get hostCountryControl() {
    return this.form.get('hostCountry');
  }
  get externalIdControl() {
    return this.form.get('externalId');
  }
  get gdprConsentControl() {
    return this.form.get('gdprConsent');
  }

  private shouldShow(controlName: string): boolean {
    const c = this.form.get(controlName);
    return !!c && (c.touched || c.dirty || this.submitted);
  }

  get firstNameError(): string | null {
    if (!this.shouldShow('firstName')) return null;
    const c = this.firstNameControl!;
    if (c.hasError('required'))
      return this.translate.instant('beneficiaries.form.firstName.error.required');
    if (c.hasError('maxlength'))
      return this.translate.instant('beneficiaries.form.firstName.error.maxLength', {
        max: this.limits.firstNameMax,
      });
    return null;
  }

  get lastNameError(): string | null {
    if (!this.shouldShow('lastName')) return null;
    const c = this.lastNameControl!;
    if (c.hasError('required'))
      return this.translate.instant('beneficiaries.form.lastName.error.required');
    if (c.hasError('maxlength'))
      return this.translate.instant('beneficiaries.form.lastName.error.maxLength', {
        max: this.limits.lastNameMax,
      });
    return null;
  }

  get emailError(): string | null {
    if (!this.shouldShow('email')) return null;
    const c = this.emailControl!;
    if (c.hasError('required'))
      return this.translate.instant('beneficiaries.form.email.error.required');
    if (c.hasError('email'))
      return this.translate.instant('beneficiaries.form.email.error.invalid');
    if (c.hasError('minlength'))
      return this.translate.instant('beneficiaries.form.email.error.minLength', {
        min: this.limits.emailMin,
      });
    if (c.hasError('maxlength'))
      return this.translate.instant('beneficiaries.form.email.error.maxLength', {
        max: this.limits.emailMax,
      });
    return null;
  }

  get phoneError(): string | null {
    if (!this.shouldShow('phone')) return null;
    const c = this.phoneControl!;
    if (c.hasError('required'))
      return this.translate.instant('beneficiaries.form.phone.error.required');
    if (c.hasError('minlength'))
      return this.translate.instant('beneficiaries.form.phone.error.minLength', {
        min: this.limits.phoneMin,
      });
    if (c.hasError('maxlength'))
      return this.translate.instant('beneficiaries.form.phone.error.maxLength', {
        max: this.limits.phoneMax,
      });
    if (c.hasError('pattern'))
      return this.translate.instant('beneficiaries.form.phone.error.invalid');
    return null;
  }

  get birthDateError(): string | null {
    if (!this.shouldShow('birthDate')) return null;
    const c = this.birthDateControl!;
    if (c.hasError('required'))
      return this.translate.instant('beneficiaries.form.birthDate.error.required');
    if (c.hasError('futureDate'))
      return this.translate.instant('beneficiaries.form.birthDate.error.future');
    return null;
  }

  get externalIdError(): string | null {
    if (!this.shouldShow('externalId')) return null;
    const c = this.externalIdControl!;
    if (c.hasError('maxlength'))
      return this.translate.instant('beneficiaries.form.externalId.error.maxLength', {
        max: this.limits.externalIdMax,
      });
    return null;
  }
}
